import { HttpHeaders } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";
import { tap } from "rxjs/operators";
import { ProduktionsmallHandelssortimentDTO } from "~app/common/models/produktionsmall/v2/ProduktionsmallHandelssortimentDTO";
import { ProduktionsmallQueryResponseDTO } from "~app/common/models/produktionsmall/v2/ProduktionsmallQueryResponseDTO";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ICommandManager } from "~app/common/services/cqs/commandManager";
import { DateUtils as dateUtils } from "~app/common/utils/date-utils";
import { ICKLeverantorList } from "~app/features/forsaljning/ckleverator/models/ckLeverantorList";
import { IPlaneraBasdataResponse } from "~app/features/planera/model/IPlaneraBasdataResponse";
import { IProduktionsomraden } from "~app/features/planera/model/IProduktionsomraden";
import { IProduktionsmall, ProduktionsmallProduktinstruktion, ProduktionsmallProduktvalRow } from "~app/features/planera/produktionsmallar/produktionsmall-produktval.models";
import { IHandelssortimentsgrupp, IProduktvalRow } from "~app/features/planera/produktval/produktval.models";

@Injectable({
    providedIn: "root"
})
export class ProduktionsmallarService {
    ckleverantorer$: BehaviorSubject<ICKLeverantorList[]>;
    handelssortimentgrupper$: BehaviorSubject<IHandelssortimentsgrupp[]>;
    produktionsMallar$: BehaviorSubject<IProduktionsmall[]>;
    produktionsomraden$: BehaviorSubject<IProduktionsomraden[]>;

    private readonly apiClient = inject(ApiClientService);

    constructor() {
        this.produktionsMallar$ = new BehaviorSubject<IProduktionsmall[]>([]);
        this.produktionsomraden$ = new BehaviorSubject<IProduktionsomraden[]>([]);
        this.ckleverantorer$ = new BehaviorSubject<ICKLeverantorList[]>([]);
        this.handelssortimentgrupper$ = new BehaviorSubject<IHandelssortimentsgrupp[]>([]);
    }

    getAllMallar(isCKMall: boolean): Observable<IProduktionsmall[]> {
        // eslint-disable-next-line sonarjs/no-selector-parameter
        if(isCKMall)  {
            return this.apiClient.get<ProduktionsmallQueryResponseDTO[]>("CKLeverantor/GetProduktionsMallar")
                .pipe(tap(x => {
                    this.produktionsMallar$.next(x as IProduktionsmall[]);
                })) as Observable<IProduktionsmall[]>;
        } else {
            return this.apiClient.get<ProduktionsmallQueryResponseDTO[]>("mina/produktionsmallar")
                .pipe(tap(x => {
                    this.produktionsMallar$.next(x as IProduktionsmall[]);
                })) as Observable<IProduktionsmall[]>;
        }
    }

    getCKLeverantorer(): Observable<any> {
        const params = { aktiv: true };
        return this.apiClient.post<any, ICKLeverantorList[]>("CKLeverantor/SearchForCKLeverantor", params, false, this.getHeader());
    }

    getHandelssortimentGrupper(produktionsomradeId: number): Observable<IHandelssortimentsgrupp[]> {
        const params = {
            produktionsomradeId: produktionsomradeId,
            periodFrom: dateUtils.getStartOfMonth(),
            periodTom: dateUtils.getStartOfNextMonth()
        };

        return this.apiClient.get<IHandelssortimentsgrupp[]>("Planera/GetHandelssortimentForProduktionsomrade", true, params);
    }

    produktvalRowFromHandelssortiment(
        mall: ProduktionsmallQueryResponseDTO,
        hs: ProduktionsmallHandelssortimentDTO,
        commandManager: ICommandManager): IProduktvalRow {
        const row = new ProduktionsmallProduktvalRow(commandManager, mall.guid);
        row.namn = hs.namn;
        row.produktinstruktioner = [];
        row.handelssortimentId = hs.handelssortimentId;
        row.handelssortimentsgrupp = hs.handelssortimentsgruppNamn;
        row.hasProduktionsmal = hs.hasProduktionsmal;

        return row;
    }

    produktvalRowsFromMall(mall: ProduktionsmallQueryResponseDTO, commandManager: ICommandManager): IProduktvalRow[] {
        const result: IProduktvalRow[] = [];
        mall.handelssortiment.forEach(hs => {
            if (hs.produkter.length > 0) {
                hs.produkter.forEach(prod => {
                    const row = new ProduktionsmallProduktvalRow(commandManager, mall.guid);
                    row.produktinstruktioner = prod.produktinstruktioner.map(g => new ProduktionsmallProduktinstruktion(g.produktinstruktionId, g.namn, g.puid));
                    row.produkt = prod.namn;
                    row.handelssortimentId = hs.handelssortimentId;
                    row.produktId = prod.produktId;
                    const utbytessimulerad = prod.produktinstruktioner.filter(g => g.medIUtbytessimulering)[0];
                    row.medTillUtbytessimulering = utbytessimulerad && utbytessimulerad.produktinstruktionId;
                    row.namn = hs.namn;
                    row.handelssortimentsgrupp = hs.handelssortimentsgruppNamn;

                    result.push(row);
                });
            } else {
                result.push(this.produktvalRowFromHandelssortiment(mall, hs, commandManager));
            }

        });

        return result;
    }

    refreshHandelssortimentGrupper(): void {
        const params = {
            produktionsomradeId: undefined,
            periodFrom: dateUtils.getStartOfMonth(),
            periodTom: dateUtils.getStartOfNextMonth()
        };

        this.apiClient.get<IHandelssortimentsgrupp[]>("Planera/GetHandelssortimentForProduktionsomrade", true, params).subscribe(x => {
            this.handelssortimentgrupper$.next(x);
        });
    }

    refreshProduktionsmallar(): void {
        this.apiClient.get<ProduktionsmallQueryResponseDTO[]>("mina/produktionsmallar").subscribe(x => {
            this.produktionsMallar$.next(x as IProduktionsmall[]);
        });
    }

    refreshProduktionsomraden(): void {
        this.apiClient.get<IPlaneraBasdataResponse>("Planera/GetPlaneraBasdata", true).subscribe(x => {
            this.produktionsomraden$.next(x.produktionsomraden);
        });
    }

    private getHeader(): HttpHeaders {
        const headers = new HttpHeaders();
        return headers.append("content-type", "application/json");
    }
}
